import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function ClassAnalytics({ attendanceData, classData, periodData, dateRange }) {
  // Calculate class-level attendance statistics
  const calculateClassAnalytics = () => {
    console.log('ClassAnalytics - attendanceData:', attendanceData);
    console.log('ClassAnalytics - classData:', classData);
    console.log('ClassAnalytics - periodData:', periodData);
    console.log('ClassAnalytics - dateRange:', dateRange);
    
    if (!attendanceData?.records) {
      console.log('No records found in attendanceData');
      return null;
    }

    // Group records by student
    const recordsByStudent = {};
    
    attendanceData.records.forEach(record => {
      const studentId = record.student?.id || record.studentId;
      const studentName = record.student?.first_name && record.student?.last_name 
        ? `${record.student.first_name} ${record.student.last_name}`
        : `Student ${studentId}`;
      
      if (!recordsByStudent[studentId]) {
        recordsByStudent[studentId] = {
          name: studentName,
          records: []
        };
      }
      recordsByStudent[studentId].records.push(record);
    });

    console.log('Records by student:', recordsByStudent);

    // Calculate statistics for each student
    const studentStats = Object.entries(recordsByStudent).map(([studentId, data]) => {
      const studentRecords = data.records;
      const totalRecords = studentRecords.length;
      
      const presentCount = studentRecords.filter(r => r.status.toUpperCase() === 'PRESENT').length;
      const lateCount = studentRecords.filter(r => r.status.toUpperCase() === 'LATE').length;
      const absentCount = studentRecords.filter(r => r.status.toUpperCase() === 'ABSENT').length;
      
      const attendanceRate = totalRecords > 0 ? ((presentCount + lateCount) / totalRecords) * 100 : 0;
      
      return {
        studentId,
        name: data.name,
        totalRecords,
        presentCount,
        lateCount,
        absentCount,
        attendanceRate
      };
    });

    // Calculate overall class statistics
    const totalStudents = studentStats.length;
    const averageAttendanceRate = totalStudents > 0 
      ? studentStats.reduce((sum, stat) => sum + stat.attendanceRate, 0) / totalStudents 
      : 0;
    
    // Calculate total counts across all students
    const totalPresentCount = studentStats.reduce((sum, stat) => sum + stat.presentCount, 0);
    const totalLateCount = studentStats.reduce((sum, stat) => sum + stat.lateCount, 0);
    const totalAbsentCount = studentStats.reduce((sum, stat) => sum + stat.absentCount, 0);
    
    const result = {
      studentStats,
      totalStudents,
      averageAttendanceRate,
      totalPresentCount,
      totalLateCount,
      totalAbsentCount,
      totalRecords: attendanceData.records.length
    };

    console.log('Class analytics result:', result);
    return result;
  };

  // Prepare chart data for student attendance comparison
  const prepareChartData = () => {
    const analytics = calculateClassAnalytics();
    if (!analytics || !analytics.studentStats.length) return null;

    // Sort students by attendance rate for better visualization
    const sortedStudents = [...analytics.studentStats].sort((a, b) => b.attendanceRate - a.attendanceRate);
    
    const labels = sortedStudents.map(student => student.name);
    const attendanceRates = sortedStudents.map(student => student.attendanceRate);
    
    // Create color coding based on attendance rate
    const backgroundColors = attendanceRates.map(rate => {
      if (rate >= 95) return '#22c55e'; // Excellent - green
      if (rate >= 85) return '#84cc16'; // Good - light green  
      if (rate >= 70) return '#f59e0b'; // Fair - yellow
      if (rate >= 50) return '#f97316'; // Poor - orange
      return '#ef4444'; // Very poor - red
    });

    return {
      labels,
      datasets: [
        {
          label: 'Attendance Rate %',
          data: attendanceRates,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors.map(color => color),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }
      ],
      studentStats: sortedStudents // Store for tooltip access
    };
  };

  const chartData = prepareChartData();
  const classAnalytics = calculateClassAnalytics();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: `Class Attendance Overview - ${classData?.name || 'Class'}`,
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#1e293b'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const studentData = chartData.studentStats[dataIndex];
            const rate = context.parsed.y.toFixed(1);
            
            return [
              `${studentData.name}: ${rate}% attendance`,
              `Total records: ${studentData.totalRecords}`,
              `Present: ${studentData.presentCount}, Late: ${studentData.lateCount}, Absent: ${studentData.absentCount}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Attendance Rate'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Students'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    }
  };

  if (!attendanceData || !classData) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📊</span>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">Class Analytics</h3>
          <p className="text-slate-600 mb-6">
            Select a class and ensure attendance data is loaded to view class-level analytics.
          </p>
        </div>
      </div>
    );
  }

  // Check if we have no attendance records for the selected period/class
  if (!classAnalytics || (attendanceData?.records && attendanceData.records.length === 0)) {
    const isPeriodSpecific = periodData;
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">
            {isPeriodSpecific ? 'No Reports Found for This Period' : 'No Reports Found'}
          </h3>
          <p className="text-slate-600 mb-2">
            {isPeriodSpecific 
              ? `No attendance records found for ${periodData.name} in the selected date range.`
              : `No attendance records found for ${classData.name} in the selected date range.`
            }
          </p>
          {isPeriodSpecific && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-800">
                <p><span className="font-semibold">Period:</span> {periodData.name}</p>
                {periodData.startTime && periodData.endTime && (
                  <p><span className="font-semibold">Time:</span> {periodData.startTime} - {periodData.endTime}</p>
                )}
              </div>
            </div>
          )}
          <p className="text-sm text-slate-500 mt-4">
            Try selecting a different {isPeriodSpecific ? 'period or ' : ''}date range to view attendance data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <p className="text-slate-600">
          {periodData ? 'Period-specific' : 'Class'} attendance overview for  <span className="font-semibold text-purple-600">{classData.name} {periodData?.name && `• ${periodData.name}`}</span>
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {classData.subject?.name && `Subject: ${classData.subject.name}`}
          {/*periodData && ` • Period: ${periodData.name}`*/}
          {periodData?.startTime && periodData?.endTime && ` (${periodData.startTime} - ${periodData.endTime})`}
          {!periodData && classData.period?.name && ` • Period: ${classData.period.name}`}
        </p>
        {/*periodData && (
          <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Filtering by Session: {periodData.name}
          </div>
        )*/}
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
        {/* Total Students Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20v-2a3 3 0 00-3-3h-4a3 3 0 00-3 3v2m4-8a3 3 0 110-6 3 3 0 010 6z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {classAnalytics.totalStudents}
              </div>
              <div className="text-sm text-blue-500 font-medium">
                students
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Total Students</h3>
            <p className="text-xs text-blue-600">
              Students with attendance records in this class for the selected school days
            </p>
          </div>
        </div>

        {/* Average Attendance Card */}
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-600">
                {classAnalytics.averageAttendanceRate.toFixed(1)}%
              </div>
              <div className="text-sm text-purple-500 font-medium">
                average rate
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-purple-800 mb-1">Class Average</h3>
            <p className="text-xs text-purple-600">
              Overall attendance rate
            </p>
            <div className="mt-2">
              <div className="w-full bg-purple-100 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${Math.min(100, classAnalytics.averageAttendanceRate)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Present Count Card */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">
                {classAnalytics.totalPresentCount}
              </div>
              <div className="text-sm text-green-500 font-medium">
                total records
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-green-800 mb-1">Present</h3>
            <p className="text-xs text-green-600">
              Total present attendance records
            </p>
            <div className="mt-2 text-xs text-green-700">
              {classAnalytics.totalRecords > 0 ? 
                `${((classAnalytics.totalPresentCount / classAnalytics.totalRecords) * 100).toFixed(1)}% of all records` : 
                'No data available'
              }
            </div>
          </div>
        </div>

        {/* Late Count Card */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-yellow-600">
                {classAnalytics.totalLateCount}
              </div>
              <div className="text-sm text-yellow-500 font-medium">
                total records
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-yellow-800 mb-1">Late</h3>
            <p className="text-xs text-yellow-600">
              Total late attendance records
            </p>
            <div className="mt-2 text-xs text-yellow-700">
              {classAnalytics.totalRecords > 0 ? 
                `${((classAnalytics.totalLateCount / classAnalytics.totalRecords) * 100).toFixed(1)}% of all records` : 
                'No data available'
              }
            </div>
          </div>
        </div>

        {/* Absent Count Card */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-red-600">
                {classAnalytics.totalAbsentCount}
              </div>
              <div className="text-sm text-red-500 font-medium">
                total records
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-800 mb-1">Absent</h3>
            <p className="text-xs text-red-600">
              Total absent attendance records
            </p>
            <div className="mt-2 text-xs text-red-700">
              {classAnalytics.totalRecords > 0 ? 
                `${((classAnalytics.totalAbsentCount / classAnalytics.totalRecords) * 100).toFixed(1)}% of all records` : 
                'No data available'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Student Attendance Chart */}
      {chartData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Student Attendance Comparison</h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                <span className="text-slate-600">Excellent (95%+)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-lime-500 rounded mr-2"></div>
                <span className="text-slate-600">Good (85-94%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
                <span className="text-slate-600">Fair (70-84%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
                <span className="text-slate-600">Poor (50-69%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                <span className="text-slate-600">Critical (&lt;50%)</span>
              </div>
            </div>
          </div>
          <div className="h-96">
            <Bar data={chartData} options={chartOptions} />
          </div>
          <div className="mt-4 text-sm text-slate-600">
            <p>Each bar represents a student's overall attendance rate for the selected period.</p>
            <p>Students are sorted by attendance rate (highest to lowest). Click on bars to see detailed breakdown.</p>
          </div>
        </div>
      )}

      {/* Student Details Table */}
      {/*classAnalytics && classAnalytics.studentStats.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Student Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Student</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Total Records</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Present</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Late</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Absent</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {classAnalytics.studentStats
                  .sort((a, b) => b.attendanceRate - a.attendanceRate)
                  .map((student) => (
                    <tr key={student.studentId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-900">{student.name}</td>
                      <td className="text-center py-3 px-4 text-slate-600">{student.totalRecords}</td>
                      <td className="text-center py-3 px-4 text-green-600 font-medium">{student.presentCount}</td>
                      <td className="text-center py-3 px-4 text-yellow-600 font-medium">{student.lateCount}</td>
                      <td className="text-center py-3 px-4 text-red-600 font-medium">{student.absentCount}</td>
                      <td className="text-center py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          student.attendanceRate >= 95 ? 'bg-green-100 text-green-800' :
                          student.attendanceRate >= 85 ? 'bg-lime-100 text-lime-800' :
                          student.attendanceRate >= 70 ? 'bg-yellow-100 text-yellow-800' :
                          student.attendanceRate >= 50 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {student.attendanceRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        
      )*/}
    </div>
  );
}
